import type { FormSegment, SegmentTreeNode } from '../../../types.ts';

/**
 * Recursively prunes empty branches from the tree.
 * A branch is considered empty if it has no leaf nodes or all its child branches are empty.
 */
const pruneEmptyBranches = (nodes: SegmentTreeNode[]): SegmentTreeNode[] => {
  let i = 0;

  while (i < nodes.length) {
    const node = nodes[i];

    if (node.children.length > 0) {
      pruneEmptyBranches(node.children);
    }

    const isValid = node.is_leaf || node.children.length > 0;

    if (!isValid) {
      nodes.splice(i, 1);
    } else {
      i++;
    }
  }

  return nodes;
};

export const buildFullSegmentTree = <K extends keyof FormSegment>(
  allSegments: FormSegment[],
  filter?: { key: K; value: FormSegment[K] }
): SegmentTreeNode[] => {
  const nodeMap = new Map<string, SegmentTreeNode>();
  const roots: SegmentTreeNode[] = [];

  // First pass: create all nodes, but filter out leaf nodes that don't match filter
  allSegments.forEach(segment => {
    // Skip leaf nodes that don't match the filter criteria
    if (filter && segment.is_leaf && segment[filter.key] !== filter.value) {
      return;
    }

    // Create a node for this segment
    nodeMap.set(segment.id, { ...segment, children: [] });
  });

  // Second pass: build the tree structure
  nodeMap.forEach((node: SegmentTreeNode) => {
    const parentId = node.parent_segment ?? '';
    const parentNode = nodeMap.get(parentId);

    if (parentNode) {
      parentNode.children.push(node);
    } else {
      roots.push(node);
    }
  });

  // If we're filtering, prune any branches that don't have matching leaf nodes
  if (filter) {
    return pruneEmptyBranches(roots);
  }

  return roots;
};
